import { useState } from "react";

import {
  faEye,
  faFile,
  faPen,
  faTrashCan,
  faUpload,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { FileDownloadButton, FileViewer } from "src/files/FileViewer";
import { FileUploadController } from "src/manualReview/FileUploadController";
import {
  FILE_EXTENSIONS,
  FILE_TYPES,
  getFileNameParts,
  humanReadableSize,
} from "src/utils/files";

type Props = {
  onFileSelect: (file: File, key: string) => void;
  onFileRemove: (key: string) => void;
  onFileNameChange: (name: string, key: string) => void;
  isUploading: boolean;
  data?: File | undefined;
  isError: boolean;
  key: string;
  disabled?: boolean;
};

const getDropzoneIcon = (data?: File) => {
  if (!data) {
    return (
      <div className="mr-2 flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100">
        <Icon color="text-indigo-600" icon={faUpload} />
      </div>
    );
  }
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-lg">
      <Icon color="text-indigo-600" icon={faFile} />
    </div>
  );
};

const FileTitle: React.FC<{
  data: File;
  onFileNameChange: (name: string, key: string) => void;
  fileKey: string;
}> = ({ data, onFileNameChange, fileKey }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { nameWithoutExt, extension } = getFileNameParts(data.name);

  const handleSave = (value: string) => {
    setIsEditing(false);
    onFileNameChange(`${value}${extension}`, fileKey);
  };

  if (isEditing) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <input
          className="pr-1 font-inter-semibold-13px text-gray-800"
          defaultValue={nameWithoutExt}
          autoFocus
          onBlur={(e) => handleSave(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSave((e.target as HTMLInputElement).value);
            } else if (e.key === "Escape") {
              setIsEditing(false);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span
        className="cursor-text truncate font-inter-semibold-13px text-gray-800 hover:bg-gray-100"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
      >
        {nameWithoutExt}
        <span className="text-gray-800">{extension}</span>
      </span>
      <Icon
        color="text-gray-500 hover:text-gray-700"
        icon={faPen}
        size="xs"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
      />
    </div>
  );
};

const getDropzoneDescription = (data?: File) => {
  if (!data) {
    return "Supported file types: " + Object.values(FILE_TYPES).join(", ");
  }
  return `${Object.values(FILE_EXTENSIONS)
    .find((ext) => data.name.toLowerCase().endsWith(`.${ext}`))
    ?.toUpperCase()} · ${humanReadableSize(data.size)}`;
};

export const FileDropzone: React.FC<Props> = ({
  onFileSelect,
  onFileRemove,
  onFileNameChange,
  disabled,
  isError,
  isUploading,
  data,
  key,
}) => {
  return (
    <FileUploadController
      disabled={isUploading || Boolean(data) || disabled}
      uploadKey={key}
      onFileSelect={(file) => onFileSelect(file, key)}
    >
      <div
        className={twJoin(
          "w-full rounded-md",
          data || isError ? "border" : "border border-dashed",
          isError && "border-red-600 ring-2 ring-red-400/25",
        )}
      >
        <div
          className={`flex justify-between ${data ? "gap-2 p-2" : "gap-4 p-4"}`}
        >
          <div className="flex min-w-0 gap-1">
            {getDropzoneIcon(data)}
            <div className="flex min-w-0 flex-col">
              <span className="font-inter-semibold-13px text-gray-800">
                {data ? (
                  <FileTitle
                    data={data}
                    fileKey={key}
                    onFileNameChange={onFileNameChange}
                  />
                ) : (
                  "Drag a file here or click to upload"
                )}
              </span>
              <span className="font-inter-normal-12px text-gray-500">
                {getDropzoneDescription(data)}
              </span>
              <span className="font-inter-normal-12px text-gray-500"></span>
              {!data && (
                <div className="mt-2">
                  <Button disabled={disabled} size="sm" variant="secondary">
                    Upload a file
                  </Button>
                </div>
              )}
            </div>
          </div>
          {data && (
            <div className="flex shrink-0 items-start">
              <FileViewer file={data}>
                <Icon
                  color="text-gray-500 hover:text-gray-700 pr-1"
                  icon={faEye}
                  size="xs"
                />
              </FileViewer>
              <FileDownloadButton file={data} />
              <Icon
                color="text-gray-500 hover:text-gray-700"
                icon={faTrashCan}
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onFileRemove(key);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </FileUploadController>
  );
};
